import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEBOUNCE_RATE = 250;
const ANIMATION_DURATION_SEC = 0;

export type SearchV2Props = {
    collapsed?: boolean;

    /**
     * An initial value for the input.
     */
    defaultValue?: string;

    /**
     * The value of the placeholder attribute on the input element.
     */
    placeholder?: string;

    /**
     * The shortcut key to focus on the search bar.
     */
    shortcutKey?: string;

    /**
     * Ref passthrough to allow the input element to focus
     */
    inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;

    /**
     * Size of the search
     */
    size?: 'small' | 'regular';

    /**
     * Callback function invoked when the value is changed. (Debounced)
     */
    onChange?: (value: string) => void;

    onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;

    onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
};

function SearchV2({
    collapsed = false,
    defaultValue,
    placeholder,
    shortcutKey,
    onChange,
    inputRef,
    onBlur,
    onFocus,
    size = 'regular',
}: SearchV2Props): JSX.Element {
    const [search, setSearch] = useSearchParams();
    const [value, setValue] = useState<string | undefined>(search.get('search') || defaultValue);

    const defaultRef = useRef<HTMLInputElement>();

    const ref = inputRef || defaultRef;

    /**
     * Checks if a key press matches the provided shortcut key and focuses the user onto the input.
     *
     * Also clears the current input value.
     */
    const detectHotKey = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                ref.current.blur();
                return;
            }

            if (!shortcutKey) {
                return;
            }
            const correctKey = e.key?.toLowerCase() === shortcutKey.toLowerCase();
            const inputElements = ['[object HTMLTextAreaElement]', '[object HTMLInputElement]'];
            // @ts-ignore
            const isContentEditable = e.target.isContentEditable;

            if (correctKey && !inputElements.includes(e.target.toString()) && !isContentEditable) {
                e.preventDefault();
                if (ref !== undefined && ref.current !== undefined) {
                    ref.current.focus();
                }
            }
        },
        [ref, shortcutKey]
    );

    useEffect(() => {
        /**
         * Configure the input element to listen for keyboard events
         */
        if (shortcutKey) {
            document.addEventListener('keydown', detectHotKey);
            return (): void => document.removeEventListener('keydown', detectHotKey);
        }
    }, [detectHotKey, shortcutKey]);

    /**
     * Whenever the value changes don't fire the onChange event until the timeout has completed.
     * Every-time a new value is entered; reset the timeout
     */
    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (value !== undefined && onChange) {
            timeout = setTimeout(() => {
                onChange(value);
                if (value !== (search.get('search') || undefined)) {
                    setSearch(
                        (s) => {
                            value ? s.set('search', value) : s.delete('search');
                            return s;
                        },
                        { replace: true }
                    );
                }
            }, DEBOUNCE_RATE);
        }

        return (): void => timeout && clearTimeout(timeout);
    }, [value, onChange, setSearch, search]);

    useEffect(() => {
        const urlSearch = search.get('search') || undefined;
        setValue(urlSearch);
    }, [onChange, search]);

    return (
        <motion.div initial={false} className={clsx(`relative h-[30px] border  border-gray-300 shadow-sm rounded-md`)}>
            {collapsed && (
                <span
                    className="absolute top-0 left-0 w-full flex items-center justify-center h-full cursor-pointer z-10 bg-white rounded-md"
                    onClick={(): void => {
                        ref.current?.focus();
                    }}
                >
                    <MagnifyingGlassIcon className="text-gray-500 h-3.5 w-3.5" />
                </span>
            )}
            <div className={clsx(`absolute inset-y-0 flex items-center justify-center pointer-events-none left-2 w-fit`)}>
                <span className="text-gray-500 h-3.5 w-3.5">
                    <MagnifyingGlassIcon />
                </span>
            </div>

            <motion.input
                initial={false}
                animate={{ paddingRight: collapsed ? 0 : 36, paddingLeft: collapsed ? 0 : 36, opacity: collapsed ? 0 : 100 }}
                transition={{
                    duration: ANIMATION_DURATION_SEC,
                    opacity: { duration: 0, delay: collapsed ? false : ANIMATION_DURATION_SEC / 2 },
                }}
                ref={ref}
                className={clsx(
                    `border-none rounded-md h-full py-2 text-sm text-gray-500 outline-none focus:ring-2 focus:ring-indigo-600 block w-full`,
                    collapsed && 'cursor-pointer'
                )}
                placeholder={placeholder || 'Search...'}
                onChange={(e): void => setValue(e.target.value)}
                value={value || ''}
                onFocus={onFocus}
                onBlur={onBlur}
                type="text"
            />

            {shortcutKey && (
                <motion.div
                    initial={false}
                    animate={{ opacity: collapsed ? '0' : '100' }}
                    transition={{ delay: collapsed ? 0 : ANIMATION_DURATION_SEC }}
                    className={clsx(`absolute inset-y-0 right-0 flex py-1.5 pr-1.5`)}
                >
                    <kbd className="inline-flex items-center border border-gray-200 rounded px-2 text-xs font-sans font-medium text-gray-500">
                        {shortcutKey.toUpperCase()}
                    </kbd>
                </motion.div>
            )}
        </motion.div>
    );
}
export default SearchV2;
